// holdingRows — map the wire holdings of /v1/wallets/:address/portfolio onto
// the rows HoldingsTable renders. Pure, so the arithmetic is testable without
// mounting anything.
//
// Honesty rules, in order of importance:
//   • Nothing is invented. A figure the backend cannot supply renders as an
//     em-dash, never as zero and never as a sample.
//   • `price_available` is the backend's own statement that it could not price
//     a position. When it is false, value and unrealized PnL are unknown — the
//     row still shows what IS known (balance, cost basis, realized PnL).
//   • Market-cap denominations (the ⇄ switchers) need total supply, which this
//     endpoint does not carry. They render an em-dash rather than a wrong number.

import type { PnLReason } from '../api/pairs'
import type { WireHolding } from '../api/portfolio'
import type {
  HistoryPosition,
  HistoryStatus,
  Holding,
  RingSpec,
} from '../components/portfolio/types'
import {
  formatPositionAmountExact,
  formatPriceAxiom,
  formatTradeAmountExact,
  formatTradeRawAmount,
  hasRealTokenIdentity,
  plainDecimal,
  shortAddr,
  shouldDisplayTicker,
} from './format'
import { searchPairPath } from './pairRoutes'
import { reasonText } from './pnlStatus'

const DASH = '—'

function num(v: string | undefined): number | null {
  if (v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function usd(v: string | undefined): string {
  const n = num(v)
  if (n === null || v === undefined) return DASH
  return `$${formatMoney(n, v, false)}`
}

function axiomUsd(v: string | undefined): string {
  const n = num(v)
  if (n === null) return DASH
  if (n === 0) return '$0'
  return `$${formatPriceAxiom(n)}`
}

// A holding's value and price (operator, 2026-11-03): a measured zero is
// "$0.00"; any nonzero figure keeps its significant digits however small — a
// drained pool's holding is "many zeros", never "$0" (formatMoney's dust floor
// is for token quantities and PnL residue, not for what a holding is worth).
function holdingUsd(v: string | undefined, format: (n: number, v: string) => string): string {
  const n = num(v)
  if (n === null || v === undefined) return DASH
  if (n === 0) return '$0.00'
  if (n > 0 && n < 1e-9) return `$${formatPriceAxiom(n)}`
  return `$${format(n, v)}`
}

function signedUsd(v: string | undefined): string {
  const n = num(v)
  if (n === null || v === undefined) return DASH
  const body = formatMoney(Math.abs(n), v.trim().replace(/^[+-]/, ''), false)
  if (n > 0) return `+$${body}`
  if (n < 0) return `-$${body}`
  return `$${body}`
}

/** Live reprice used to write `String(n)`, which is `1e-18` below 1e-6. The
    table formatters only accept a plain decimal, so a known dust PnL rendered
    as "—" above "-0.00%". Expand first; below one nano say 0, not "we do
    not know". */
function formatMoney(n: number, raw: string, signed: boolean): string {
  const decimal = /[eE]/.test(raw) ? plainDecimal(n) : raw.trim()
  if (Math.abs(n) > 0 && Math.abs(n) < 1e-9) {
    return formatPositionAmountExact(decimal, signed)
  }
  return formatTradeAmountExact(decimal, signed)
}

function tokenAmount(raw: string | undefined, decimals: number | undefined): string {
  if (
    raw === undefined ||
    !/^(0|[1-9]\d*)$/.test(raw) ||
    !Number.isInteger(decimals) ||
    (decimals as number) < 0 ||
    (decimals as number) > 18
  )
    return DASH
  return formatTradeRawAmount(raw, decimals as number)
}

function rawQty(raw: string | undefined, decimals: number | undefined): number | null {
  if (
    raw === undefined ||
    !/^(0|[1-9]\d*)$/.test(raw) ||
    !Number.isInteger(decimals) ||
    (decimals as number) < 0 ||
    (decimals as number) > 18
  )
    return null
  const n = Number(raw) / 10 ** (decimals as number)
  return Number.isFinite(n) ? n : null
}

// Remaining-cost / remaining-balance (avg_entry_*) counts attached TON as if
// it were the entry price, so Avg MC comes out tens of times the live cap.
// Quote average is what was actually spent on tokens / tokens received.
function quoteAvgUsd(h: WireHolding): string | undefined {
  const bought = num(h.bought_usd)
  const qty = rawQty(h.bought_token_raw, h.token_decimals)
  if (bought !== null && bought > 0 && qty !== null && qty > 0) {
    return String(bought / qty)
  }
  return h.avg_entry_usd
}

function quoteAvgMc(h: WireHolding): string | undefined {
  const avg = num(quoteAvgUsd(h))
  const price = num(h.current_price_usd)
  const mcap = num(h.current_mcap_usd)
  if (avg !== null && avg > 0 && price !== null && price > 0 && mcap !== null) {
    return String(mcap * (avg / price))
  }
  return h.avg_entry_mcap_usd
}

/** What the sales realized. The dollars need no price (they were booked at
 *  each sale's rate); the percent is in GRAM, as the backend books a sale
 *  (calculator.go applySell: realized = proceeds − the sold part's cost −
 *  the fee), so what the sold part cost is proceeds − realized. Nothing sold
 *  is a dash, not a 0%. */
function realizedFigures(h: WireHolding): {
  realizedUsd: string
  realizedPct: string
  realizedUp: boolean
} {
  const usdValue = num(h.realized_pnl_usd)
  const soldTon = num(h.sold_ton)
  const realizedTon = num(h.realized_pnl_ton)
  const sold = soldTon !== null && soldTon > 0
  const cost = sold && realizedTon !== null ? (soldTon as number) - realizedTon : null
  // A realized 0 is a zero, said as one ($0, 0%), only where it is known to
  // be one (r9x-3, the operator: "if empty, zeros instead of dashes"):
  // nothing was sold — by the units sold when the read has them, else by the
  // GRAM — or the server says the realized figure is the whole of it. A 0
  // that is only the known part of sales whose cost is partly unknown is not
  // known: a dash (r9x-5 review F1).
  const soldRaw = h.sold_token_raw?.trim()
  const nothingSold = soldRaw ? soldRaw === '0' : soldTon === 0
  const zeroKnown = usdValue === 0 && (nothingSold || h.realized_complete === true)
  const shown = usdValue !== null && (usdValue !== 0 || zeroKnown)
  return {
    realizedUsd: shown ? signedUsd(h.realized_pnl_usd) : DASH,
    realizedPct:
      cost !== null && cost > 0
        ? pct(realizedTon, cost)
        : zeroKnown && nothingSold
          ? pct(0, 1)
          : DASH,
    realizedUp: (usdValue ?? 0) >= 0,
  }
}

/** The market cap now against the one at the average entry. */
function mcapChange(h: WireHolding): { mcapChange: string; mcapUp: boolean | null } {
  const entry = h.price_available ? num(quoteAvgMc(h)) : null
  const now = h.price_available ? num(h.current_mcap_usd) : null
  if (entry === null || now === null || entry <= 0) return { mcapChange: DASH, mcapUp: null }
  const change = now / entry - 1
  return { mcapChange: pct(now - entry, entry), mcapUp: change >= 0 }
}

function pct(numerator: number | null, denominator: number | null): string {
  if (numerator === null || denominator === null || denominator === 0) return DASH
  const p = (numerator / denominator) * 100
  if (!Number.isFinite(p)) return DASH
  return `${p >= 0 ? '+' : ''}${p.toFixed(2)}%`
}

// holdTime renders seconds as the coarsest unit that stays readable.
export function holdTime(seconds: number | null | undefined): string {
  // unknown is a bare "—", no words (the operator, 2026-11-03)
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return DASH
  if (seconds < 60) return `${Math.floor(seconds)}s`
  const m = Math.floor(seconds / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

// ringFor mirrors SearchModal's construction exactly: launchpad_source is what
// TokenAvatar reads as "migrated", so it may only be set once the token has
// actually graduated. A still-bonding token instead passes its launchpad
// through `dex`, which keeps the launchpad colour and the real bonding arc.
export function ringFor(
  h: Pick<
    WireHolding,
    'launchpad_source' | 'launchpad_status' | 'bonding_progress_pct' | 'primary_dex'
  >
): RingSpec {
  const source = h.launchpad_source || null
  const migrated = source !== null && h.launchpad_status === 'migrated'
  return {
    dex: migrated ? (h.primary_dex ?? undefined) : (source ?? h.primary_dex ?? undefined),
    launchpadSource: migrated ? source : null,
    bondingPct: source !== null ? (h.bonding_progress_pct ?? 0) : null,
  }
}

// Below this USD value a position is "dust" for the Hide-small toggle.
const SMALL_POSITION_USD = 1

export type HoldingFilters = {
  /** Matches ticker, name, or the master address. */
  query: string
  hideSmall: boolean
  /** Inclusive USD floor when Hide small is on. Empty → $1. */
  minUsd?: number | null
  /** Inclusive USD ceiling when Hide small is on. Empty → no ceiling. */
  maxUsd?: number | null
}

/**
 * Wire holdings → sorted, filtered table rows. Hide-small drops only positions
 * we can actually price: an unpriced position has no value to compare against,
 * and hiding it would silently swallow a holding the user owns.
 */
export function buildHoldingRows(holdings: WireHolding[], filters: HoldingFilters): Holding[] {
  rememberTokenFaces(holdings)
  const needle = filters.query.trim().toLowerCase()
  const visible = holdings
    .filter((h) => {
      // A quarantined row has identity and no figures: it is drawn as such —
      // every figure "—" (unknown, the operator 2026-11-03), no Sell — never
      // as $0 / 0s (the SKITTY/MTONGA lie), and never dropped (positions
      // L5: a held token no quantity source answered for is one of these;
      // dropped, it vanished from the list without a word)
      if (h.position_unavailable) return true
      const balance = num(h.balance)
      return balance === null || balance > 0
    })
    .filter((h) => {
      if (!filters.hideSmall) return true
      // Careful: Number('') is 0, and 0 is finite — reading an absent value as
      // zero would classify every unpriced position as dust and hide it.
      if (!h.price_available) return true
      const value = num(h.current_value_usd)
      if (value === null) return true
      const min = filters.minUsd == null ? SMALL_POSITION_USD : filters.minUsd
      if (value < min) return false
      if (filters.maxUsd != null && value > filters.maxUsd) return false
      return true
    })
    .map((holding) => toHoldingRow(holding))

  return visible.filter((r) => {
    if (!needle) return true
    return (
      r.ticker.toLowerCase().includes(needle) ||
      r.name.toLowerCase().includes(needle) ||
      r.id.toLowerCase().includes(needle)
    )
  })
}

/** Why a position's unrealized and total PnL are left out: no known cost
    (positions reader, basis_unknown / held_basis_unknown) — its words, the
    first letter raised; undefined otherwise. */
function pnlWhyOf(h: WireHolding): string | undefined {
  const code = (h.pnl_reasons ?? []).find(
    (r) => r === 'held_basis_unknown' || r === 'basis_unknown'
  )
  if (!code) return undefined
  const words = reasonText(code as PnLReason)
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`
}

export function toHoldingRow(h: WireHolding): Holding {
  const ticker = shouldDisplayTicker(h.token_ticker, h.token_master)
    ? h.token_ticker
    : shortAddr(h.token_master)
  const name = h.token_name?.trim() ?? ''

  const balance = num(h.balance)
  const costBasis = num(h.remaining_cost_basis_usd)
  const acquisitionBasis = num(h.acquisition_basis_usd)
  const unrealized = h.price_available ? num(h.unrealized_pnl_usd) : null
  // Remaining USD is restated at the current TON/USD rate (handler.go:514).
  // Acquisition USD is what was actually paid. With no sells the dollars of
  // PnL are the same, but the two denominators are not — GroypUtya 2026-08-16
  // read +48.37% unrealized vs +48.12% total, sold_usd=0, rem_ton=acq_ton.
  // Unrealized % was the GRAM return wearing a USD hat. Same basis as Total
  // (and as the pair Rail) until a sale splits remaining from acquired.
  const soldUsd = num(h.sold_usd)
  const soldTon = num(h.sold_ton)
  const noSells = (soldUsd === null || soldUsd === 0) && (soldTon === null || soldTon === 0)
  const unrealizedBasis = noSells && acquisitionBasis !== null ? acquisitionBasis : costBasis
  // Total PnL = realized + unrealized. Without a price the unrealized half is
  // unknown, so the total is unknown too — showing the realized half under a
  // "Total PnL" heading would understate a loss (or overstate a gain) on a
  // position the user still holds. The realized figure is reported honestly in
  // the Spot summary's own Realized PnL row.
  const total = h.price_available ? num(h.total_pnl_usd) : null

  return {
    id: h.token_master,
    path: searchPairPath({
      master: h.token_master,
      pool: h.primary_pool_addr,
      dex: h.primary_dex,
      launchpadSource: h.launchpad_source,
      launchpadStatus: h.launchpad_status,
    }),
    ticker,
    name,
    // A blank deploy's image_url is a shared placeholder — render the monogram.
    imageUrl: hasRealTokenIdentity(h.token_name, h.token_ticker, h.token_master)
      ? (h.token_image_url ?? undefined)
      : undefined,
    ring: ringFor(h),
    stage: h.launchpad_source && h.launchpad_status === 'bonding' ? 'bonding' : 'dex',

    balanceUsd: h.price_available
      ? holdingUsd(h.current_value_usd, (n, v) => formatMoney(n, v, false))
      : DASH,
    balanceTokens: balance === null ? DASH : formatTradeAmountExact(h.balance.trim()),

    avgBuy: axiomUsd(quoteAvgUsd(h)),
    avgBuyMc: h.price_available ? usd(quoteAvgMc(h)) : DASH,
    price:
      h.price_available && num(h.current_price_usd) !== null
        ? holdingUsd(h.current_price_usd, (n) => formatPriceAxiom(n))
        : DASH,
    mcap: h.price_available ? usd(h.current_mcap_usd) : DASH,
    ...mcapChange(h),

    boughtUsd: usd(h.bought_usd),
    boughtTokens: tokenAmount(h.bought_token_raw, h.token_decimals),
    soldUsd: usd(h.sold_usd),
    soldTokens: tokenAmount(h.sold_token_raw, h.token_decimals),

    pnlUsd: h.price_available ? signedUsd(h.unrealized_pnl_usd) : DASH,
    pnlPct: pct(unrealized, unrealizedBasis),
    pnlUp: (unrealized ?? 0) >= 0,

    totalPnlUsd: h.price_available ? signedUsd(h.total_pnl_usd) : DASH,
    totalPnlPct: pct(total, acquisitionBasis),
    totalPnlUp: (total ?? 0) >= 0,
    pnlWhy: pnlWhyOf(h),

    ...realizedFigures(h),

    holding: holdTime(h.hold_time_seconds),
    lastActivityAt:
      h.last_activity_at && Number.isFinite(Date.parse(h.last_activity_at))
        ? h.last_activity_at
        : undefined,
    firstAcquiredAt:
      h.first_acquired_at && Number.isFinite(Date.parse(h.first_acquired_at))
        ? h.first_acquired_at
        : undefined,
    ...(h.position_unavailable ? { unavailable: true } : {}),
    ...(h.client_wallets ? { wallets: h.client_wallets } : {}),
    ...(h.client_held_in ? { heldIn: h.client_held_in } : {}),
    sort: {
      balance: h.price_available ? num(h.current_value_usd) : null,
      bought: num(h.bought_usd),
      sold: num(h.sold_usd),
      pnl: h.price_available ? num(h.unrealized_pnl_usd) : null,
      totalPnl: h.price_available ? num(h.total_pnl_usd) : null,
      realized: num(h.realized_pnl_usd),
      hold:
        typeof h.hold_time_seconds === 'number' &&
        Number.isFinite(h.hold_time_seconds) &&
        h.hold_time_seconds >= 0
          ? h.hold_time_seconds
          : null,
      mcap: h.price_available ? num(h.current_mcap_usd) : null,
      price: h.price_available ? num(h.current_price_usd) : null,
    },
  }
}

function positionStatus(h: WireHolding): HistoryStatus {
  const balance = num(h.balance)
  const sold = rawQty(h.sold_token_raw, h.token_decimals)
  const bought = rawQty(h.bought_token_raw, h.token_decimals)
  if (balance === 0) return 'sold_all'
  if (bought !== null && sold !== null && sold >= bought && (balance === null || balance <= 0)) {
    return 'sold_all'
  }
  if ((num(h.sold_usd) ?? 0) > 0 || (sold !== null && sold > 0)) return 'partial'
  return 'holding'
}

function closedHoldSeconds(h: WireHolding): number | undefined {
  const first = h.first_acquired_at ? Date.parse(h.first_acquired_at) : Number.NaN
  const last = h.last_activity_at ? Date.parse(h.last_activity_at) : Number.NaN
  if (Number.isFinite(first) && Number.isFinite(last) && last >= first) {
    return Math.floor((last - first) / 1000)
  }
  return typeof h.hold_time_seconds === 'number' &&
    Number.isFinite(h.hold_time_seconds) &&
    h.hold_time_seconds >= 0
    ? h.hold_time_seconds
    : undefined
}

/**
 * Tokens this wallet bought — including fully sold ones. Holdings hides
 * balance 0; History is the round-trip ledger.
 */
function lastActivityMs(h: WireHolding): number {
  const ms = h.last_activity_at ? Date.parse(h.last_activity_at) : Number.NaN
  return Number.isFinite(ms) ? ms : 0
}

const tokenFaces = new Map<string, string>()
const FACES_STORE = 'wils.tokenfaces.v1'

function hydrateTokenFaces(): void {
  if (tokenFaces.size > 0) return
  try {
    const raw = localStorage.getItem(FACES_STORE)
    if (!raw) return
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return
    for (const [master, url] of Object.entries(parsed as Record<string, unknown>)) {
      if (master && typeof url === 'string' && url) tokenFaces.set(master, url)
    }
  } catch {
    /* quota / private mode */
  }
}

function persistTokenFaces(): void {
  try {
    localStorage.setItem(FACES_STORE, JSON.stringify(Object.fromEntries(tokenFaces)))
  } catch {
    /* quota / private mode */
  }
}

hydrateTokenFaces()

export function rememberTokenFace(master: string, imageUrl: string): void {
  if (!master || !imageUrl || tokenFaces.get(master) === imageUrl) return
  tokenFaces.set(master, imageUrl)
  persistTokenFaces()
}

export function rememberTokenFaces(holdings: WireHolding[]): void {
  let dirty = false
  for (const h of holdings) {
    if (
      h.token_master &&
      h.token_image_url &&
      tokenFaces.get(h.token_master) !== h.token_image_url
    ) {
      tokenFaces.set(h.token_master, h.token_image_url)
      dirty = true
    }
  }
  if (dirty) persistTokenFaces()
}

export function tokenFaceImage(master: string | undefined): string | undefined {
  return master ? tokenFaces.get(master) : undefined
}

export function buildHistoryRows(holdings: WireHolding[], query: string): HistoryPosition[] {
  rememberTokenFaces(holdings)
  const needle = query.trim().toLowerCase()
  return holdings
    .filter((h) => {
      if (h.position_unavailable) return false
      const bought = rawQty(h.bought_token_raw, h.token_decimals)
      const sold = rawQty(h.sold_token_raw, h.token_decimals)
      const boughtUsd = num(h.bought_usd)
      const soldUsd = num(h.sold_usd)
      return (
        (bought !== null && bought > 0) ||
        (sold !== null && sold > 0) ||
        (boughtUsd !== null && boughtUsd > 0) ||
        (soldUsd !== null && soldUsd > 0)
      )
    })
    .sort((a, b) => lastActivityMs(b) - lastActivityMs(a))
    .map((h) => {
      const row = toHoldingRow(h)
      const status = positionStatus(h)
      const closed = status === 'sold_all'
      return {
        ...row,
        status,
        statusLabel:
          status === 'sold_all' ? 'Sold All' : status === 'partial' ? 'Partial' : 'Holding',
        balanceUsd: closed ? '$0' : row.balanceUsd,
        balanceTokens: closed ? '0' : row.balanceTokens,
        holding: holdTime(closed ? closedHoldSeconds(h) : h.hold_time_seconds),
        holdFrozen: closed,
        sort: {
          ...row.sort,
          balance: closed ? 0 : row.sort.balance,
          hold: closed ? (closedHoldSeconds(h) ?? null) : row.sort.hold,
        },
      }
    })
    .filter((r) => {
      if (!needle) return true
      return (
        r.ticker.toLowerCase().includes(needle) ||
        r.name.toLowerCase().includes(needle) ||
        r.id.toLowerCase().includes(needle)
      )
    })
}
